/**
 * Defines a container that displays a single child at a time and allows the user to select which child is visible
 * by clicking on tabs.  This is an alternative to dijit/layout/TabContainer.  This TabContainer is similar to the
 * dijit offering but adds the following features:
 *
 * <ul>
 *    <li>ability to reposition tabs on-the-fly</li>
 *    <li>ability to drag the tabs to any border</li>
 *    <li>ability to reorder tabs by dragging</li>
 *    <li>support for dragging children in and out of the container</li>
 *    <li>support for a "plus" button</li>
 *    <li>integrated tab scrolling and overflow menu</li>
 *    <li>options for succession following tab close/remove</li>
 *    <li>option to automatically truncate tab labels to fit</li>
 *    <li>eliminates some tab rendering issues</li>
 *    <li>eliminates loss of scroll position when switching tabs on IE</li>
 * </ul>
 *
 * The TabContainer's behavior is governed by a number of client settable properties.  These properties may be fixed,
 * meaning that they should be set at construction time and not changed or dynamic meaning that they can be
 * changed at any time.  Since the TabContainer inherits the "Stateful" mixin, get, set and watch methods
 * can be used to manage and track dynamic properties.
 *
 * @module MW/layout/TabContainer
 * @copyright Copyright 2015-2018 The MathWorks, Inc.
 */
define([
    "dojo/_base/declare",
    "dojo/_base/event",
    "dojo/_base/lang",
    "dojo/dom",
    "dojo/dom-attr",
    "dojo/dom-class",
    "dojo/dom-construct",
    "dojo/dom-geometry",
    "dojo/dom-style",
    "dojo/Evented",
    "dojo/keys",
    "dojo/mouse",
    "dojo/on",
    "dojo/Stateful",
    "dojo/touch",
    "dijit/focus",
    "dijit/Menu",
    "dijit/MenuItem",
    "dijit/popup",
    "dijit/_TemplatedMixin",
    "dijit/_WidgetBase",
    "dijit/layout/_LayoutWidget",
    "MW/layout/ButtonBar",
    "MW/layout/Geometry",
    "MW/layout/HidingContainer",
    "MW/layout/LayoutChildProperty",
    "MW/layout/LayoutContainerEvent",
    "MW/layout/LayoutUtils",
    "MW/layout/Restorable",
    "MW/layout/SelectionBlocker",
    "MW/layout/SelectionManager",
    "MW/layout/StatefulExtension",
    "MW/layout/Traversable",
    "MW/layout/TabBar",
    "mw-form/mixins/property/TagMixin",
    "dojo/text!./templates/TabPlusButton.html"
], function (declare, event, lang, dom, domAttr, domClass, domConstruct, domGeometry, domStyle, Evented, keys, mouse, on,
             Stateful, touch, focus, Menu, MenuItem, popup,  _TemplatedMixin, _WidgetBase, _LayoutWidget, ButtonBar,
             Geometry, HidingContainer, LayoutChildProperty, LayoutContainerEvent, LayoutUtils, Restorable, SelectionBlocker,
             SelectionManager, StatefulExtension, Traversable, TabBar, TagMixin, TabPlusButtonTemplate) {

    /**
     * @classdesc Defines a plus button that will optionally appear following the tabs
     * @class TabPlusButton
     * @private
     */
    var TabPlusButton = declare([_WidgetBase, _TemplatedMixin], {

        templateString: TabPlusButtonTemplate,

        buildRendering: function () {
            var that = this;
            this.inherited(arguments);
            this._setActionsAttr(this.actions);
            dom.setSelectable(this.domNode, false);
            LayoutUtils.preventFocus(this.domNode);

            on(this.plusNode, "click", function (e) {
                that.actions[0].callback(e);
            });

            on(this.dropDownNode, touch.press, lang.hitch(this, "_displayPlusMenu"));
        },

        _setActionsAttr: function (actions) {
            this._set("actions", actions);
            domStyle.set(this.dropDownNode, {display: this.actions.length < 2 ? "none" : "inline"});
            if (this.actions.length > 0) {
                domAttr.set(this.domNode, "title", this.actions[0].text);
            }
        },

        _displayPlusMenu: function () {
            var i, menu, action, item, clickHandler, menuCloseHandler;
            SelectionBlocker.blockSelection(this.domNode);
            popup.close();
            menu = new Menu({id: this.id + "PlusMenu"});
            menuCloseHandler = function () {
                menu.destroyRecursive();
                SelectionBlocker.unblockSelection();
            };
            for (i = 0; i < this.actions.length; i++) {
                action = this.actions[i];
                clickHandler = function (actionToExecute) {
                    return function (e) {
                        actionToExecute.callback(e);
                        popup.close();
                    }
                }(action);
                item = new MenuItem({
                    label: action.text,
                    onClick: clickHandler
                });
                on(item.domNode, touch.release, clickHandler);
                menu.addChild(item);
            }
            popup.open({
                around: this.dropDownNode,
                popup: menu,
                onClose: menuCloseHandler,
                onCancel: function() {popup.close()}
            });
            focus.focus(menu.domNode);
        }
    });

    /**
     * Dynamic properties
     */
    var TAB_POSITION = "tabPosition";
    var SHRINK_TABS_TO_FIT = "shrinkTabsToFit";
    var MINIMUM_TAB_WIDTH = "minimumTabWidth";
    var MAXIMUM_TAB_WIDTH = "maximumTabWidth";
    var HIDE_SINGLE_TAB = "hideSingleTab";
    var SUCCESSION_OPTION = "successionOption";
    var SHOWING_CHILD = "showingChild";
    var PLUS_ACTIONS = "plusActions";
    var TAB_BACKGROUND = "tabBackground";
    var TAB_FOREGROUND = "tabForeground";
    var SHOWING_TAB_BACKGROUND = "showingTabBackground";
    var SHOWING_TAB_FOREGROUND = "showingTabForeground";

    var MINIMUM_VERTICAL_TABS_WIDTH = 65;  // Width needed to accommodate grip and actions drop down
    var DEFAULT_TAB_POSITION = "top";
    var DEFAULT_SHRINK_TABS_TO_FIT = false;
    var DEFAULT_MAXIMUM_TAB_WIDTH = undefined;

    /**
     *  Succession options
     */
    var NONE = "none";
    var MOST_RECENT = "mostRecent";
    var NEIGHBOR = "neighbor";

    var BAR_COLOR_SELECTOR             = ".mwTabContainer .tabBar";
    var TAB_COLOR_SELECTOR             = ".mwTabContainer .tab:not(.checkedTab)";
    var SHOWING_COLOR_SELECTOR         = ".mwTabContainer .tab.checkedTab";
    var SHOWING_BOTTOM_BORDER_SELECTOR = ".mwTabContainer .tab.bottomOpen.checkedTab";
    var SHOWING_TOP_BORDER_SELECTOR    = ".mwTabContainer .tab.topOpen.checkedTab";
    var SHOWING_LEFT_BORDER_SELECTOR   = ".mwTabContainer .tab.leftOpen.checkedTab";
    var SHOWING_RIGHT_BORDER_SELECTOR  = ".mwTabContainer .tab.rightOpen.checkedTab";
    var SELECTED_BAR_COLOR_SELECTOR    = ".mwTabContainer .tabBar.selected";
    var SELECTED_TAB_COLOR_SELECTOR    = ".mwTabContainer .tabBar.selected .tab:not(.checkedTab)";

    /**
     * @class MW/layout/TabContainer
     * @classdesc Defines a layout widget that can have any number of children, displays one child at a time and
     * allows the user select which child is displayed.
     */
    var TabContainer = declare([_LayoutWidget, Evented, Restorable, Traversable, HidingContainer, StatefulExtension, TagMixin], {
        /**
         * @property {boolean} displayToolTipsOnTruncatedTabs - true if the tab label should appear as a tool tip if there is
         *                                                      insufficient space to display it on the tab
         * @property {boolean} isContentBorderVisible - true if a border should surround the displayed child
         * @property {boolean} selectionHighlightingEnabled - true if color or some other indication should be used to highlight
           *                                                  the showing tab if it is also "selected"
         * @property {boolean} tabReorderingEnabled - true if the user should be able to reorder tabs by dragging
         * @property {boolean} tabsRepositioningEnabled - true if the user should be able to drag the tabs from one edge to another
         * @property {boolean} tabWidthAdjustmentEnabled - true if when the tabs are on the left or right the user should
         *                                                 to adjust their width by dragging
         * @property {boolean} distributeTabs - true if the tabs should be distributed so as to occupy the entire container width
         * @property {Object} tabClass - an optional class to be used to instantiate tabs.  This class should generally extend Tab.
         *
         * @instance
         */
        Fixed: undefined,  // Trick to make the JSDoc generator list the above under "Fixed"
        // The following properties can be specified at construction time but subsequent changes will be ignored
        displayToolTipsOnTruncatedTabs: false,
        isContentBorderVisible: true,
        selectionHighlightingEnabled: false,
        tabReorderingEnabled: false,
        tabsRepositioningEnabled: false,
        tabWidthAdjustmentEnabled: false,
        plusDropDownEnabled: true,

        baseClass: "mwTabContainer",

       /**
        * @property {string} tabPosition - Edge along which tabs will appear ("top", "left", "bottom" or "right")
        * @property {boolean} shrinkTabsToFit: - true if tab labels should automatically be truncated if necessary
        *                                        to forestall the need to scroll tabs
        * @property {boolean} hideSingleTab - true if the tab area should be hidden when it contains only one entry and
        *                                     does not contain the optional plus button.
        * @property {number} minimumTabWidth - controls the minimum tab width.  This is relevant when the tabs are on
        *                                      the top or bottom and shrinkTabsToFit is true as well as when tabs are on
        *                                      the left or right regardless of the shrinkTabsToFit setting.
        * @property {boolean} reserveSpaceForTabs - true if the tab area should be reserved and visible even when it is
        *                                           empty, containing neither visible tabs nor the optional plus button
        * @property {string} successionOption - indicates which child should appear following the removal of the
        *                                       currently showing child.  Options include:
        *    <ul>
        *        <li>"most recent" (default) the child that was most recently shown will appear</li>
        *        <li>"neighbor" the child associated with the tab to the immediate left of the removed tab will appear, unless there is no tab
        *            to the left, in which case the child associated with the tab to the immediate right will appear.</li>
        *        <li>"none" no child will appear until some action is taken to show one</li>
        *    </ul>
        * @property {Object} showingChild - reflects/controls the showing child. The object can be either a child _Widget
        *                                   child title string.
        * @property {array} plusActions - when the array is non-empty a "+" button that triggers the first action
        *                                 in the array will appear following the tabs.  If the array contains more than
        *                                 one action a drop down arrow will also appear.
        * @property {boolean} tabAnimationEnabled - true if tab comings and goings should be animated
        * @property {string} tabBackground - the background color of tabs other than the one corresponding to the showing
        *                                    child (string should contain CSS color specifier)
        * @property {string} tabForeground - the foreground color of tabs other than the one corresponding to the showing
        *                                    child (string should contain CSS color specifier)
        * @property {string} showingTabBackground - the background color of the tab corresponding to the showing child
        *                                    (string should contain CSS color specifier)
        * @property {string} showingTabForeground - the foreground color of the tab corresponding to the showing child
        *                                    (string should contain CSS color specifier)
        * @instance
        */
        Dynamic: undefined,  // Trick to make the JSDoc generator list the above under "Dynamic"

        tabPosition: DEFAULT_TAB_POSITION,
        shrinkTabsToFit: DEFAULT_SHRINK_TABS_TO_FIT,
        hideSingleTab: false,
        successionOption: MOST_RECENT,
        tabAnimationEnabled: true,
        tabBarClass: TabBar,

        _showOrder: 0,
        _areTabsHidden: false,

        /* _tabInfo: array that stores the following per TabContainer child
           widget
           properties
           showOrder
         */
        // _showingInfo
        // _showBox
        // _styleSheet  Only used if custom colors specified

        constructor: function () {
            this._tabInfo = [];
            this.plusActions = [];

            delete this.Fixed;   // Not a real property just a JSDoc trick
            delete this.Dynamic; // Not a real property just a JSDoc trick
        },

        buildRendering: function () {
            var that = this;
            this.inherited(arguments);
            domClass.add(this.domNode, "mwLayoutContainer");
            LayoutUtils.setLocaleOnDomNode(this.domNode);

            // Create an internal container div that will provide a full or partial border around the
            // child widgets.
            this.containerNode = domConstruct.create("div", {id: this.id + "Wrapper"}, this.domNode);
            this._updateContainerBorder();

            // Create the tab bar and add it to the DOM.  Not adding the tab bar as a child widget keeps the
            // inherited child array clean, i.e. it contains only widgets that correspond to tabs.
            this._tabBar = new this.tabBarClass({
                id: this.id + "_tablist",  // for consistency with Dijit TabContainer
                orientation: this._orientationFromTabPosition(this.tabPosition),
                displayToolTipsWhenTruncated: this.displayToolTipsOnTruncatedTabs,
                reorderingEnabled: this.tabReorderingEnabled,
                shrinkWidthToFit: this.shrinkTabsToFit,
                animationEnabled: this.tabAnimationEnabled,
                tabOpening: Geometry.oppositeRegion(this.tabPosition),
                distributeButtons: this.distributeTabs,
                selectionHighlightingEnabled: this.selectionHighlightingEnabled
            });
            if (this.tabClass) {
                this._tabBar.tabClass = this.tabClass;
            }
            this.tablist = this._tabBar;  // Alias to provide some degree of backward compatibility with Dijit TabContainer
            this._tabBar.placeAt(this.domNode);
            dom.setSelectable(this._tabBar.domNode, false);

            this._tabBar.on(LayoutContainerEvent.CLOSE, lang.hitch(this, "_tabCloseHandler"));
            this._tabBar.on(LayoutContainerEvent.STATE_CHANGED, function () {
                that.emit(LayoutContainerEvent.STATE_CHANGED);
            });
            this._tabBar.watch(ButtonBar.SHRINK_WIDTH_TO_FIT, function (name, oldValue, newValue) {
                that.notifyStateWatchers(SHRINK_TABS_TO_FIT, oldValue, newValue);
                that.layout();
            });
            this._tabBar.watch(ButtonBar.MINIMUM_BUTTON_WIDTH, function (name, oldValue, newValue) {
                that.notifyStateWatchers(MINIMUM_TAB_WIDTH, oldValue, newValue);
                that.layout();
            });

            if (this.tabsRepositioningEnabled) {
                this._horizontalBarGrip = domConstruct.create("div", {id: this.id + "HGrip"});
                domClass.add(this._horizontalBarGrip, "horizontalBarGrip");
                dom.setSelectable(this._horizontalBarGrip, false);
                LayoutUtils.preventFocus(this._horizontalBarGrip, true);

                this._verticalBarGrip = domConstruct.create("div", {id: this.id + "VGrip"});
                domClass.add(this._verticalBarGrip, "verticalBarGrip");
                dom.setSelectable(this._verticalBarGrip, false);
                LayoutUtils.preventFocus(this._verticalBarGrip, true);

                var beginDragFunction = lang.hitch(this, "_beginTabsDrag");
                on(this._horizontalBarGrip, touch.press, beginDragFunction);
                on(this._verticalBarGrip, touch.press, beginDragFunction);
                on(this._tabBar.domNode, touch.press, beginDragFunction);
            }

            if (this.tabWidthAdjustmentEnabled) {
                // Add edge cover to support tab width adjustment by dragging when tabs are on left or right
                this._tabBarEdgeCover = domConstruct.create("div", {id : this.id + "TabBarEdgeCover"}, this.domNode);
                domClass.add(this._tabBarEdgeCover, "edgeCoverV");
                on(this._tabBarEdgeCover, touch.press, lang.hitch(this, "_beginTabBarEdgeDrag"));
                this._updateTabBarEdgeCoverVisibility();
            }
        },

        postCreate: function () {
            var that = this;
            this.inherited(arguments);
            if (this._pendingStyles) {
                this._pendingStyles.forEach(function (pendingStyle) {
                    that._addCustomStyle(pendingStyle.selector, pendingStyle.style);
                });
                delete this._pendingStyles;
            }
        },

        startup: function () {
            if (this._started) {return; }

            var that = this;
            this.getChildren().forEach(function (child) {
                that._setupChild(child);
            });

            this.inherited(arguments);

            this._updateBarGrip(this._tabBar.orientation);

            // Since the tab bar isn't a child widget it needs to be explicitly started here.
            this._updateTabsVisibility();
            this._tabBar.startup();

            this.own(on(this._tabBar.domNode, "keydown", lang.hitch(this, this._handleKeyDown)));

            // For bottom Container tab navigation
            var collapseAllButton;
            if (this.getParent && this.getParent() && this.getParent()._collapseAllButton) {
                collapseAllButton = this.getParent()._collapseAllButton;
            }

            if (collapseAllButton) {
                this.own(on(collapseAllButton, "keydown", lang.hitch(this, this._handleKeyDown)));
            }
        },

        destroy: function () {
            if (this._styleSheet) {
                document.head.removeChild(this._styleSheet);
            }
            this.inherited(arguments);
        },

        /**
         * Adds a child to the container.  The child will be managed in accordance with the
         * provided properties.  If the properties object is Stateful then the container will update the values
         * of properties designated as "dynamic" below.  It will also respond to changes made to these properties.
         * Hence, for example, if the user reorders the tabs by dragging the affected index properties will be updated.
         * Conversely if the some code outside the container changes a child's index property its tab will be
         * repositioned accordingly.
         *
         * @param widget {_Widget} the child to add
         * @param properties {Object} an object containing any of the following properties:
         * @param properties.title {String} - Displayed on tab corresponding to child
         * @param properties.closable {boolean} - Determines whether the child will be "closed" in response to appropriate
         *        gestures on its tab
         * @param properties.canClose (function} - will be called in response to a close gesture providing an opportunity for
         *        veto before proceeding.  The function is expected to return either a boolean or a Promise which will
         *        eventually be resolved (to ascent to close) or rejected (to veto close)
         * @param properties.description {String} - Displayed as a tooltip when the mouse hovers over the coresponding tab
         * @param properties.index (number) - the relative position of the tab corresponding to the widget
         * @param index {number} the relative position of the tab corresponding to the widget
         * @param show {boolean} set false to prevent the child from being shown upon add
         * @param immediate {boolean} set false top allow animation or batching of operations.  This might delay
         *        completion of the add
         * @instance
         */
        addWithProperties: function (widget, properties, index, show, immediate) {
            var tabInfo = this._createTabInfo(widget, properties);
            tabInfo.initialIndex = index;
            tabInfo.immediate = immediate;
            this._tabInfo.push(tabInfo);
            this.addChild(widget, index, show, immediate);
        },

        /**
         * This is a less flexible alternative to the addWithProperties function.
         * It is compatible with the Dijit TabContainer.  Properties related to the container's handling of the child,
         * will be obtained directly from the widget.
         *
         * This method overrides _LayoutWidget.addChild
         *
         * @param widget {_Widget} the child to add
         * @param index (number) the relative position of the tab corresponding to the widget
         * @param show {boolean} set false to prevent the child from being shown upon add
         * @param immediate {boolean} set false to allow animation or batching of operations.  This might delay
         *        completion of the add
         * @instance
         */
        addChild: function (widget, index, show, immediate) {
            var tabInfo = this._getTabInfoFromDomId(widget.id);
            if (!tabInfo) {
                tabInfo = this._createTabInfo(widget);
                tabInfo.initialIndex = index;
                tabInfo.immediate = immediate;  // Used in setupChild
                this._tabInfo.push(tabInfo);
            }
            var state = this._pendingState;
            if (state) {
                if (this._updateInfoFromState(state, tabInfo)) {
                    tabInfo.immediate = true;
                    show = this._tabInfo.length === 1 ||
                           !!(state.showingChildId && state.showingChildId === tabInfo.properties.childId) ||
                           !!(state.showingChildTitle && state.showingChildTitle === tabInfo.properties.title);
                    state.toRestoreCount--;
                } else {
                    state.toRestoreCount = 0;
                }
                if (state.toRestoreCount === 0) {
                    this.endStateRestore();
                }
            }

            if (show === false) {
                tabInfo.properties.set(LayoutChildProperty.IS_SHOWING, false);
            }
            this._updateTabsVisibility();  // One would think this call need only be made after calling the inherited method
                                           // but if it is not called here a problem occurs during drag into the bottom
                                           // TabContainer of a ArrangeableBorderContainer (see geck 1536926).

            // Don't pass arguments other than the widget being added to the inherited method.  In particular don't
            // pass the index (see geck 1651362).
            arguments.length = 1;
            this.inherited(arguments);
            this.emit(LayoutContainerEvent.CHILD_ADDED, tabInfo.widget, tabInfo.properties);

            widget.navAssociation = this._tabBar;
            widget.sideBarExpandedNavAssociation = this._tabBar;

            // Set the child invisible to address geck 1261032.  The _showChild method will set it visible.
            this._setChildVisible(tabInfo, false);
            if (show !== false) {
                this._showChild(tabInfo);
            }
            this._updateTabsVisibility();

            this.layout();
        },

        /**
         * Removes the specified widget from the container
         *
         * @param widget {_Widget} the widget to be removed
         * @param immediate {boolean} set false to allow animation or batching of operations that might delay
         *        completion of the removal
         *
         * This method overrides _LayoutWidget.removeChild
         *
         * @instance
         */
        removeChild: function (widget, immediate) {
            var index = this._getLocalIndexFromDomId(widget.id);
            var tabInfo, nextInfo;
            if (index >= 0) {
                tabInfo = this._tabInfo[index];
                if (tabInfo.unbindChildProperties) {
                    tabInfo.unbindChildProperties();
                }
                tabInfo.isRemoving = true;
                // Successor chosen before remove from tab bar because successor may depend on relative tab bar order
                if (!this._suppressSuccession) {
                    nextInfo = this._chooseSuccessor(tabInfo);
                }
                this._tabBar.removeTab(tabInfo.properties, immediate);
                // If the child being removed is "selected" clear the selection background
                if (tabInfo.properties.get(LayoutChildProperty.IS_SELECTED)) {
                    domClass.remove(this._tabBar.domNode, "selected");
                }
                this._tabInfo.splice(index, 1);
                if (this.hideSingleTab && this._tabInfo.length === 1) {
                    // Also remove the single tab left behind
                    this._tabBar.removeTab(this._tabInfo[0].properties, immediate);
                }
                this._showChild(nextInfo);

                if (widget.domNode) {
                    this.inherited(arguments);
                }
                this._updateTabsVisibility();

                if (this._started) {
                    this.layout();
                    this.emit(LayoutContainerEvent.STATE_CHANGED);
                }
                // Make sure the widget is visible
                this._setChildVisible(tabInfo, true);

                // Clean up style changes made to position the child
                if (widget.domNode) {
                    domStyle.set(widget.domNode, {
                        top: "auto",
                        left: "auto",
                        position: "static"}
                    );
                }
                this.emit(LayoutContainerEvent.CHILD_REMOVED, tabInfo.widget, tabInfo.properties);
            }
        },

        /**
         * Differs from removeChild in two respects:
         * <ol>
         *    <li>If the child properties include a "canClose" function it is called first,
         *      providing an opportunity for veto.</li>
         *    <li>If the close proceeds the child is destroyed.<li>
         * </ol>
         *
         * @param widget {_Widget} the widget to be closed
         * @param force {boolean} true if the close should proceed without querying for potential veto
         * #param interactive {boolean} true if the close was initiated by clicking the tab "x"
         * @instance
         */
        closeChild: function (widget, force, interactive) {
            var approval;
            var tabInfo = this._getTabInfoFromDomId(widget.id);
            if (!tabInfo) {
                return;
            }
            // The preferred way to mediate close is to provide a canClose function that returns either a boolean or a
            // deferred but the legacy onClose function is also supported.  The key difference is that canClose is
            // strictly about permission, it should not assume the close will proceed even if assent is given.
            if (force || !tabInfo.properties.canClose) {
                if (force || !widget.onClose || widget.onClose(this, widget)) {
                    this._doClose(tabInfo);
                }
            } else {
                approval = tabInfo.properties.canClose();
                if (approval) {
                    if (typeof approval.then === "function") {
                        approval.then(lang.hitch(this, "_doClose", tabInfo),
                                                 function() {/* vetoed, do nothing */});
                    } else {
                        this._doClose(tabInfo);
                    }
                }
            }
        },

        _doClose: function (tabInfo) {
            // The following is a bit of a hack.  In the context of the document container removing a group
            // of children may result in transfer to another tab container.  A better solution should be sought.
            var widget = tabInfo.widget;
            var container = widget.getParent();
            if (container) {
                container.removeChild(widget);
                tabInfo.properties.set(LayoutChildProperty.IS_OPEN, false);
                tabInfo.properties.set(LayoutChildProperty.IS_SHOWING, false);
            }
        },

        /**
         * @returns {boolean} true if the container has no children
         * @instance
         */
        isEmpty: function () {
            return this._tabInfo.length === 0;
        },

        /**
         * @returns {number} the number of children
         * @instance
         */
        getTabCount: function () {
            return this._tabInfo.length;
        },

        /**
         * Removes (and optionally destroys) all children
         *
         * @param destroy {boolean} true
         * @instance
         */
        removeAll: function (destroy) {
            var widget;
            while (this._tabInfo.length > 0) {
                widget = this._tabInfo[0].widget;
                this.removeChild(widget);
                if (destroy) {
                    widget.destroyRecursive();
                }
            }
        },

        setSuppressSuccession: function (value) {
            this._suppressSuccession = value;
        },

        /**
         * Returns the bar displaying the tabs.
         *
         * @returns {TabBar}
         * @instance
         */
        getTabBar: function () {
            return this._tabBar;
        },

        getTabForChild: function (widget) {
            return this._tabBar.getTabForProperties(this.getChildProperties(widget));
        },

        /*
         * Returns and array containing the TabContainer children in order of tab appearance.  Note that the
         * ordering of the array returned by the inherited getChildren method will not necessarily match the tab order.
         *
         * @returns {Array}
         * @instance
         */
        getChildrenInTabOrder: function () {
            var children = [];
            var tabs, i;
            if (this._tabInfo.length === 1) {
                // If there is only one child it may not have a tab
                children.push(this._tabInfo[0].widget);
            } else {
                tabs = this._tabBar.getButtons();
                for (i = 0; i < tabs.length; i++) {
                    children.push(tabs[i].widget);
                }
            }
            return children;
        },

        /*
         * Returns and array containing the TabContainer children in order in which they were last shown
         * (most recent last)
         *
         * @returns {Array}
         * @instance
         */
        getChildrenInShowOrder: function () {
            var sortedInfo = this._tabInfo.slice(0);
            sortedInfo.sort(function (info1, info2) {
                return info1.showOrder - info2.showOrder;
            });
            var children = [];
            var i;
            for (i = 0; i < sortedInfo.length; i++) {
                children.push(sortedInfo[i].widget);
            }
            return children;
        },

        /**
         * @param titleChildIdOrWidget {Object} the title string, childId string or Widget associated with the child of interest
         * @returns {object} the properties object associated with the specified child
         * @instance
         */
        getChildProperties: function (titleChildIdOrWidget) {
            var tabInfo = this._getTabInfo(titleChildIdOrWidget);
            return tabInfo ? tabInfo.properties : null;
        },

        /**
         * Adds a child and begins a drag operation on its tab.
         *
         * @param dragOutInfo {Object} information captured when the child was dragged out of its previous parent,
         *                             including widget and properties.
         * @param mousePoint {Object} contains the x and y mouse coordinates
         * @instance
         */
        dragIn: function (dragOutInfo, mousePoint) {
            // Create and add tab info that includes drag in information before proceeding with the
            // normal addChild.  The setupChild function will forward the drag in info to the
            // tab bar.
            var tabInfo = this._createTabInfo(dragOutInfo.widget, dragOutInfo.properties);
            tabInfo.initialIndex = dragOutInfo.index;
            dragOutInfo.mousePoint = mousePoint;
            tabInfo.dragInInfo = dragOutInfo;
            tabInfo.immediate = true;
            this._tabInfo.push(tabInfo);
            this.addChild(dragOutInfo.widget);
        },

        setGripNeighbor: function (node) {
            if (node !== this._gripNeighbor) {
                if (this._gripNeighbor) {
                    LayoutUtils.removeFromParent(this._gripNeighbor);
                    domClass.remove(this._verticalBarGrip, "withNeighbor");
                }
                if (node) {
                    domConstruct.place(node, this._verticalBarGrip);
                    domClass.add(this._verticalBarGrip, "withNeighbor");
                }
                this._gripNeighbor = node;
            }
        },

        getGripNeighbor: function () {
            return this._gripNeighbor;
        },

        /**
         * Provides an DOM element to be displayed in the upper right corner of the container, on top of any child.
         *
         * @param node {Object} the DOM node to display.  Can be null, in which case any previously set node
         *                      will be removed.
         * @param showWhenTabsHidden {function} function that accepts a boolean object indicating whether any tabs are showing
         * and returns true if the overlay node should appear
         *
         * @instance
         */
        setUpperRightOverlay: function (node, shouldShowOverlayCallback) {
            this._upperRightOverlay = node;
            this._shouldShowOverlayCallback = shouldShowOverlayCallback;
            this._updateUpperRightOverlayVisibility();
            domConstruct.place(node, this.domNode);
        },

        /**
         * @returns {*} the DOM node displayed in the upper right
         * @instance
         */
        getUpperRightOverlay: function () {
            return this._upperRightOverlay;
        },

        /**
         * Displays the specified child widget.  The widget must have been previously added to the container.
         *
         * @param titleChildIdOrWidget {Object} the title string, childId string or Widget associated with the child to show
         * @instance
         */
        showChild: function (titleChildIdOrWidget) {
            this.set(SHOWING_CHILD, titleChildIdOrWidget);
        },

        /**
         * This is an alias for showChild.  This is part of the Traversable API.
         *
         * @param widget {_Widget} the widget to reveal
         * @instance
         */
        revealChild: function (widget) {
            this.showChild(widget);
        },

        /**
         * Determines the next, in tab order, child relative to the given reference child.  This is part of the Traversable API.
         *
         * @param widget {_Widget} the reference child widget
         * @param showing {boolean} if true then return a child that is already showing
         * @param forwardNavigationKeyPressed {boolean} true if the call to this function originated from
         * UIContainer when forward navigation key is pressed
         * @returns {_Widget} the next child widget
         * @instance
         */
        nextChild: function (widget, showing, forwardNavigationKeyPressed) {
            if (forwardNavigationKeyPressed) {
                showing = true;
            }
            if (showing) {
                var showingChild = this.get(SHOWING_CHILD);
                return showingChild === widget ? null : showingChild;
            }
            var children = this.getChildrenInTabOrder();
            if (!widget) {
                return children.length > 0 ? children[0] : null;
            }
            for (var i = 0; i < children.length - 1; i++) {
                if (children[i] === widget) {
                    return children[i + 1];
                }
            }
            return null;
        },

        /**
         * Determines the previous, in tab order, child relative to the given reference child.  This is part of the Traversable API.
         *
         * @param widget {_Widget} the reference child widget
         * @param showing {boolean} if true then return a child that is already showing
         * @param backwardNavigationKeyPressed {boolean} true if the call to this function originated from
         * UIContainer when backward navigation key is pressed
         * @returns {_Widget} the previous child widget
         * @instance
         */
        previousChild: function (widget, showing, backwardNavigationKeyPressed) {
            if (backwardNavigationKeyPressed) {
                showing = true;
            }
            if (showing) {
                var showingChild = this.get(SHOWING_CHILD);
                return showingChild === widget ? null : showingChild;
            }
            var children = this.getChildrenInTabOrder();
            if (!widget) {
                return children.length > 0 ? children[children.length - 1] : null;
            }
            for (var i = children.length - 1; i > 0; i--) {
                if (children[i] === widget) {
                    return children[i - 1];
                }
            }
            return null;
        },


        /** This fulfills the HidingContainer interface
         * @instance
         */
        isShowingLocally: function (widget) {
            return this._showingInfo && this._showingInfo.widget === widget;
        },

        /**
         * Returns an object that encapsulates the container's user modifiable state, including the position and order
         * of its tabs.  This object can subsequently be passed to the setState function in order to restore the container
         * to a previous state.  This is part of the Restorable API.
         *
         * @returns {Object} object encapsulating the current state
         * @instance
         */
        getState: function () {
            var i, info;
            var state = {
                children: []
            };
            state.children.length = this._tabInfo.length;

            LayoutUtils.saveIfNotDefault(state, TAB_POSITION, this, DEFAULT_TAB_POSITION);
            LayoutUtils.saveIfNotDefault(state, SHRINK_TABS_TO_FIT, this, DEFAULT_SHRINK_TABS_TO_FIT);
            LayoutUtils.saveIfNotDefault(state, MAXIMUM_TAB_WIDTH, this, DEFAULT_MAXIMUM_TAB_WIDTH);

            if (this._showingInfo) {
                info = this._showingInfo;
                if (info.properties.childId) {
                    state.showingChildId = info.properties.childId;
                } else {
                    state.showingChildTitle = info.properties.title;
                }
            }
            var children = this.getChildrenInTabOrder();
            for (i = 0; i < children.length; i++) {
                info = this._getTabInfoFromDomId(children[i].id);
                state.children[i] = {
                    showOrder: info.showOrder
                };
                if (info.properties.childId) {
                    state.children[i].id = info.properties.childId;
                } else {
                    state.children[i].title = info.properties.title;
                }
            }
            return state;
        },

        /**
         * Restores the container to a previously saved state (see getState).  If the container does not currently have
         * children that were present when the state was captured the actual restoration will
         * be deferred until such time as any missing children have been added.  This is part of the Restorable API.
         *
         * @param state {Object} object encapsulation previous state
         * @instance
         */
        setState: function (state) {
            var that = this;
            var tabInfoToShow, tabIndex,
                state = Object.assign({}, state);

            // Set container attributes.  Set those not specified to default.
            LayoutUtils.restoreOrSetDefault(state, TAB_POSITION, this, DEFAULT_TAB_POSITION);
            LayoutUtils.restoreOrSetDefault(state, SHRINK_TABS_TO_FIT, this, DEFAULT_SHRINK_TABS_TO_FIT);
            LayoutUtils.restoreOrSetDefault(state, MAXIMUM_TAB_WIDTH, this, DEFAULT_MAXIMUM_TAB_WIDTH);

            var restoredCount = 0;
            state.toRestoreCount = state.children.length;
            // Loop through saved children looking for matches among the existing children
            // Reorder the tabs associated with the existing children.
            tabIndex = 0;
            nullIdxs = [];
            state.children.forEach(function (savedChildInfo, childIdx) {
                if (savedChildInfo == null) { // handles null and undefined
                    state.toRestoreCount--;
                    nullIdxs.push(childIdx);
                    return;
                }
                
                var tabInfo, i, savedId;
                delete savedChildInfo.restored;
                for (i = 0; i < that._tabInfo.length; i++) {
                    tabInfo = that._tabInfo[i];
                    savedId = savedChildInfo.id || savedChildInfo.childId;
                    if ((savedId && savedId === tabInfo.properties.childId) ||
                        (!savedId && savedChildInfo.title === tabInfo.properties.title)) {
                        tabInfo.properties.set(LayoutChildProperty.INDEX, tabIndex++);
                        tabInfo.showOrder = savedChildInfo.showOrder;
                        savedChildInfo.restored = true;
                        state.toRestoreCount--;
                        restoredCount++;
                        if ((state.showingChildId && savedId === state.showingChildId) ||
                            (!state.showingChildId && savedChildInfo.title === state.showingChildTitle)) {
                            tabInfoToShow = tabInfo;
                        }
                        break;
                    }
                }
            });
            
            // Cleanup null indices
            nullIdxs.sort().reverse().forEach((idx) => {
                state.children.splice(idx, 1);
            });
            
            // Save state info so it can be used to order the tabs as additional children are added back
            if (state.toRestoreCount > 0 && restoredCount === this._tabInfo.length) {
                this._pendingState = state;
            }
            if (tabInfoToShow) {
                this._showChild(tabInfoToShow);
            }
        },

        /**
         * This is part of the Restorable API.
         *
         * @returns {boolean} true if the container is in the process of restoring state.
         * @instance
         */
        isRestoringState: function () {
            return !!this._pendingState;
        },

        /**
         * Terminates state restoration.  Leaving the container in its current state.
         * This is part of the Restorable API.
         *
         * @instance
         */
        endStateRestore: function () {
            delete this._pendingState;
        },

        /**
         * Returns true if the container's state is being restored and the specified widget (identified by its childId or title)
         * is included in the state being restored.
         *
         * @param childIdOrTitle {string} id or title for the widget of interest
         * @returns {boolean} true if the specified child is expected
         * @instance
         */
        isExpecting: function (childIdOrTitle) {
            return this._pendingState && TabContainer.stateIncludes(this._pendingState, childIdOrTitle);
        },

        _updateInfoFromState: function (state, tabInfo) {
            var i, savedChildInfo, savedId;
            var index = 0;
            // Loop through state children looking for childId and counting children already restored
            for (i = 0; i < state.children.length; i++) {
                savedChildInfo = state.children[i];
                savedId = savedChildInfo.id || savedChildInfo.childId;
                if ((savedId && savedId === tabInfo.properties.childId) ||
                    (!savedId && savedChildInfo.title === tabInfo.properties.title)) {
                    tabInfo.initialIndex = index;
                    tabInfo.showOrder = savedChildInfo.showOrder;
                    savedChildInfo.restored = true;
                    return true;
                }
                if (savedChildInfo.restored) {
                    index++;
                }
            }
            return false;
        },

        /**
         * Updates the container in response to resize.
         *
         * This method overrides the inherited resize method
         *
         * @instance
         */
        resize: function () {
            // Cache the computed style
            if (!this.computedStyle) {
                this.computedStyle = domStyle.getComputedStyle(this.domNode);
            }
            this.inherited(arguments);
        },

        /**
         * Positions the child widgets and the tab bar
         *
         * This method overrides the inherited layout method
         *
         * @instance
         */
        layout: function () {
            if (!this.domNode) {
                return;
            }
            var contentBox = domGeometry.getContentBox(this.domNode, this.computedStyle);
            if (!contentBox || contentBox.h === 0) {
                return;  // No height
            }

            // Allocate space for tabs and use left over space for showing child.
            var barSize, barBox, coverBox;
            var showBox = contentBox;
            var maxBarWidth;
            var that = this;
            if (!this._areTabsHidden) {
                barSize = this._tabBar.getPreferredSize();
                if (this._tabBar.orientation === "vertical") {
                    barSize.w = Math.max(barSize.w, Math.max(MINIMUM_VERTICAL_TABS_WIDTH, this._tabBar.get(ButtonBar.MINIMUM_BUTTON_WIDTH)));
                    maxBarWidth = Math.max(this.maximumTabWidth || (contentBox.w - 100), 0);
                    if (barSize.w > maxBarWidth) {
                        barSize.w = maxBarWidth;
                    } else if (this._tabInfo.length > 0) {
                        this._set(MAXIMUM_TAB_WIDTH, 0);
                    }
                }
                barBox = lang.clone(contentBox);
                switch (this.tabPosition) {
                    case "top":
                        barBox.h = barSize.h;
                        showBox.t += barBox.h;
                        showBox.h -= barBox.h;
                        break;
                    case "bottom":
                        barBox.t = contentBox.t + contentBox.h - barSize.h;
                        barBox.h = barSize.h;
                        showBox.h -= barBox.h;
                        break;
                    case "left":
                        barBox.w = barSize.w;
                        showBox.l += barBox.w;
                        showBox.w -= barBox.w;
                        coverBox = lang.clone(showBox);
                        coverBox.l -= 3;
                        coverBox.w = 7;
                        break;
                    case "right":
                        barBox.l = contentBox.l + contentBox.w - barSize.w;
                        barBox.w = barSize.w;
                        showBox.w -= barBox.w;
                        coverBox = lang.clone(barBox);
                        coverBox.l -= 3;
                        coverBox.w = 7;
                }
                this._tabBar.resize(barBox);
            }
            // Size the content
            domGeometry.setMarginBox(this.containerNode, showBox);
            this._showBox = showBox;
            if (this._showingInfo) {
                this._positionChild(this._showingInfo);
                if (this._upperRightOverlay) {
                    domGeometry.setMarginBox(this._upperRightOverlay, {l: showBox.l + showBox.w - 18,
                                                                       t: showBox.t + 2,
                                                                       w: 16, h: 16});
                }
            }
            // Position non-showing children if requested
            this._tabInfo.forEach(function (childInfo) {
                if (childInfo !== that._showingInfo && childInfo.properties.updateSizeWhenHidden) {
                    that._positionChild(childInfo);
                }
            });
            if (coverBox && this.tabWidthAdjustmentEnabled) {
                domGeometry.setMarginBox(this._tabBarEdgeCover, coverBox);
            }
        },

        _positionChild: function (tabInfo) {
            var widgetBox;
            if (this._showBox) {
                // Adjust widget box to account for container border
                if (this.isContentBorderVisible) {
                    widgetBox = {l: 0, t: 0, w: this._showBox.w - 2, h: this._showBox.h - 2};
                } else {
                    widgetBox = {l: 0, t: 0, w: this._showBox.w, h: this._showBox.h};
                    switch (this.tabPosition) {
                        case "bottom":
                            widgetBox.h -= 1;
                            break;
                        case "right":
                            widgetBox.w -= 1;
                    }
                    if (!this._areTabsHidden) {
                        // Account for border on tab edge
                        if (this.tabPosition === "top" || this.tabPosition === "bottom") {
                            widgetBox.h -= 1;
                        } else {
                            widgetBox.w -= 1;
                        }
                    }
                }
                LayoutUtils.setSize(tabInfo.widget, widgetBox);
            }
        },

        _updateBarGrip: function (orientation) {
            if (orientation === "horizontal") {
                if (this._horizontalBarGrip) {
                    this._tabBar.setPreNeighbor(this._horizontalBarGrip);
                }
            } else if (this._verticalBarGrip) {
                this._tabBar.setPreNeighbor(this._verticalBarGrip);
            }
        },

        _updateTabsVisibility: function () {
            var tabCount = this._tabInfo.length;
            var hideTabs = !(this.reserveSpaceForTabs || tabCount > 1 || (tabCount > 0 && !this.hideSingleTab) || this.plusActions.length > 0);
            if (hideTabs !== this._areTabsHidden) {
                this._areTabsHidden = hideTabs;
                domStyle.set(this._tabBar.domNode, {visibility : hideTabs ? "hidden" : "visible"});
                this._updateContainerBorder();
            }
            this._updateUpperRightOverlayVisibility();
        },

        _updateUpperRightOverlayVisibility: function () {
            if (this._upperRightOverlay && this._shouldShowOverlayCallback) {
                var noTab = this._areTabsHidden || (this.hideSingleTab && this._tabInfo.length < 2) ;
                domStyle.set(this._upperRightOverlay, {visibility: this._shouldShowOverlayCallback(!noTab) ? "visible" : "hidden"});
            }
        },

        _updateContainerBorder: function () {
            var newWrapperClass;
            if (this.isContentBorderVisible) {
                newWrapperClass = "fullTabContentWrapper";
            } else if (!this._areTabsHidden) {
                newWrapperClass = this._getContentWrapperClass(this.tabPosition);
            } else {
                newWrapperClass = "noTabContentWrapper";
            }
            if (newWrapperClass !== this._wrapperClass) {
                if (this._wrapperClass) {
                    domClass.remove(this.containerNode, this._wrapperClass);
                }
                this._wrapperClass = newWrapperClass;
                if (newWrapperClass) {
                    domClass.add(this.containerNode, newWrapperClass);
                }
            }
        },

        _updateTabBarEdgeCoverVisibility: function () {
            if (this._tabBarEdgeCover) {
                domStyle.set(this._tabBarEdgeCover, {visibility: this._tabBar.orientation === "vertical" ? "visible" : "hidden"});
            }
        },

        _getContentWrapperClass: function (tabPosition) {
            return tabPosition + "TabContentWrapper";
        },

        _createTabInfo: function (widget, properties) {
            if (properties && !(properties.watch && properties.set && properties.get)) {
                // The TabContainer requires a Stateful properties object so it can use the set and watch methods.
                // Rather than checking for an instanceof Stateful we check method existenece since the former
                // will not work across window contexts. If a non-Stateful properties object was supplied, copy the
                // property values into a Stateful one.
                var statefulProperties = new Stateful();
                lang.mixin(statefulProperties, properties);
                properties = statefulProperties;
            }
            return {
                widget: widget,
                properties: properties || widget,
                showOrder: -1
            };
        },

        _getLocalIndexFromDomId: function (id) {
            var i;
            for (i = 0; i < this._tabInfo.length; i++) {
                if (this._tabInfo[i].widget.id === id) {
                    return i;
                }
            }
            return -1;
        },

        _getTabInfo: function (titleChildIdOrWidget) {
            var tabInfo = null;
            if (typeof(titleChildIdOrWidget) === "string") {
                tabInfo = this._getTabInfoFromChildId(titleChildIdOrWidget);
                if (!tabInfo) {
                    tabInfo = this._getTabInfoFromTitle(titleChildIdOrWidget);
                }
            } else if (titleChildIdOrWidget) {
                tabInfo = this._getTabInfoFromDomId(titleChildIdOrWidget.id);
            }
            return tabInfo;
        },

        _getTabInfoFromDomId: function (id) {
            var index = this._getLocalIndexFromDomId(id);
            return index < 0 ? null : this._tabInfo[index];
        },

        _getTabInfoFromTitle: function (title) {
            var i;
            for (i = 0; i < this._tabInfo.length; i++) {
                if (this._tabInfo[i].properties.title === title) {
                    return this._tabInfo[i];
                }
            }
            return null;
        },

        _getTabInfoFromChildId: function (childId) {
            var i;
            for (i = 0; i < this._tabInfo.length; i++) {
                if (this._tabInfo[i].properties.childId === childId) {
                    return this._tabInfo[i];
                }
            }
            return null;
        },

        _setupChild: function (child) {
            this.inherited(arguments);

            domStyle.set(child.domNode, "position", "absolute");
            /* Put child in front of the tabs which overlap it so when they animate in they are hidden.
             // Alternatively the tabs could (should) be clipped.
             domStyle.set(child.domNode, "z-index", "2");
             */

            var tabInfo = this._getTabInfoFromDomId(child.id);
            var tabCount = this._tabInfo.length;
            var firstTabInfo;
            if (tabInfo && !tabInfo.childWasSetup) {
                tabInfo.childWasSetup = true;  // Prevent the following from being repeated, for example if child widget
                                               // is borrowed and returned (see LendingTabContainer)

                this._bindChildProperties(tabInfo);
                // Add a tab unless this is the first tab and the container is configured to hide single tabs
                if (this.hideSingleTab && tabCount === 2 && this._tabBar.getButtons().length === 0) {
                    firstTabInfo = this._tabInfo[0];
                    this._tabBar.addTab(firstTabInfo.widget, firstTabInfo.properties, firstTabInfo.initialIndex, tabInfo.immediate);
                }
                if ((!this.hideSingleTab || tabCount > 1) && tabInfo !== firstTabInfo) {
                    this._tabBar.addTab(child, tabInfo.properties, tabInfo.initialIndex, tabInfo.immediate, tabInfo.dragInInfo);
                }
                if (tabInfo === this._showingInfo && LayoutUtils.isShowing(this.domNode)) {
                    tabInfo.properties.set(LayoutChildProperty.IS_SHOWING, true);
                }
            }
        },

        _bindChildProperties: function (tabInfo) {
            var that = this;
            var watches = [];
            if (tabInfo.properties) {
                watches.push(tabInfo.properties.watch(LayoutChildProperty.IS_SHOWING, function (name, oldValue, newValue) {
                    if (newValue) {
                        that._showChild(tabInfo);
                    }
                }));
                watches.push(tabInfo.properties.watch(LayoutChildProperty.IS_OPEN, function (name, oldValue, newValue) {
                    if (!newValue) {
                        that._doClose(tabInfo);
                    }
                }));
                watches.push(tabInfo.properties.watch(LayoutChildProperty.TITLE, function () {
                    if (that._orientationFromTabPosition(that.tabPosition) === "vertical") {
                        // Defer layout refresh to allow TabBar to respond to title change first
                        setTimeout(function () {
                            that.layout();
                        }, 0);
                    }
                }));
                watches.push(tabInfo.properties.watch(LayoutChildProperty.IS_ENABLED, function (name, oldValue, newValue) {
                    if (that._tabInfo.length === 1) {
                        // This is the only child.  Show or hide it based on its enabled state
                        that._showChild(newValue ? tabInfo : null);
                    } else if (!newValue && tabInfo === that._showingInfo) {
                        // Show some other child
                        that._showChild(that._chooseSuccessor(tabInfo));
                    }
                }));

                tabInfo.unbindChildProperties = function () {
                    watches.forEach(function (watch) {
                        watch.remove();
                    });
                    delete tabInfo.unbindChildProperties;
                };
            }
        },

        _showChild: function (tabInfo) {
            if (this._showingInfo === tabInfo) {
                return;
            }
            var oldChild = null;
            var oldProperties;
            var isRemoving;
            if (this._showingInfo) {
                this._setChildVisible(this._showingInfo, false);
                this.selectedChildWidget = null;
                oldProperties = this._showingInfo.properties;
                oldChild = this._showingInfo.widget;
                isRemoving = this._showingInfo.isRemoving;
            }
            this._showingInfo = tabInfo;
            if (tabInfo) {
                this._setChildVisible(tabInfo, true);
                this.selectedChildWidget = tabInfo.widget;
                // The selectedChildWidget property is maintained solely for backward compatibility
                // with the Dijit TabContainer.
                tabInfo.showOrder = this._showOrder++;
                this._positionChild(tabInfo);
                if (this._started) {
                    // If the container hasn't been started then the child's tab will not have been created yet
                    // (see setupChild).  In this case we don't want set isShowing true yet, lest the
                    // TabBar miss the notification and therefore fail to check the corresponding tab.
                    tabInfo.properties.set(LayoutChildProperty.IS_SHOWING, HidingContainer.isShowingGlobally(this));
                }
            }
            if (this._started && oldProperties && !isRemoving) {
                oldProperties.set(LayoutChildProperty.IS_SHOWING, false);
            }
            this.notifyStateWatchers(SHOWING_CHILD, oldChild, tabInfo ? tabInfo.widget : null);
            // If the widget being shown has an onShow method, called it.  This is necessary for backward compatibility
            // with the Dijit TabContainer (see geck 1255251).
            if (tabInfo && tabInfo.widget.onShow) {
                tabInfo.widget.onShow();
            }
            if (this._started) {
                this.emit(LayoutContainerEvent.STATE_CHANGED);
            }
        },

        _setChildVisible: function (tabInfo, visible) {
            if (!tabInfo.widget.domNode) {
                return;  // The bridging between the MOTW layout manager and the UIContainer creates a situation where
                         // a widget is destroyed before being removed.  This works around the problem for now.
            }
            if (visible) {
                domClass.remove(tabInfo.widget.domNode, "hiddenChild");
                // The following call should not be necessary but some integration tests are explicitly
                // checking for this style.
                domStyle.set(tabInfo.widget.domNode, {visibility : "visible"});
            } else {
                domClass.add(tabInfo.widget.domNode, "hiddenChild");
                domStyle.set(tabInfo.widget.domNode, {visibility : "hidden"});
            }
        },

        _chooseSuccessor: function (tabInfo) {
            var i, tabIndex, info, maxOrder, successorInfo, tabs;
            if (this.successionOption === MOST_RECENT) {
                // Find the enabled tab (other than the one given) having highest showOrder
                maxOrder = -2;
                for (i = 0; i < this._tabInfo.length; i++) {
                    info = this._tabInfo[i];
                    if (info !== tabInfo && info.showOrder > maxOrder && info.properties.get(LayoutChildProperty.IS_ENABLED) !== false) {
                        maxOrder = info.showOrder;
                        successorInfo = info;
                    }
                }
            } else if (this.successionOption === NEIGHBOR) {
                // Find the first enabled tab that precedes or follows the given tab
                tabIndex = tabInfo.properties.get(LayoutChildProperty.INDEX);
                tabs = this._tabBar.getButtons();
                for (i = tabIndex - 1; i >= 0; i--) {
                    if (tabs[i].enabled !== false) {
                        successorInfo = this._getTabInfoFromDomId(tabs[i].widget.id);
                        break;
                    }
                }
                for (i = tabIndex + 1; !successorInfo && i < tabs.length; i++) {
                    if (tabs[i].enabled !== false) {
                        successorInfo = this._getTabInfoFromDomId(tabs[i].widget.id);
                        break;
                    }
                }
            }
            return successorInfo;
        },

        _tabCloseHandler: function (event) {
            this.closeChild(event.widget, false /* force */, true /* interactive */);
        },

        _orientationFromTabPosition: function (tabPosition) {
            return (tabPosition === "top" || tabPosition === "bottom") ? "horizontal" : "vertical";
        },

        _setTabPositionAttr: function (position) {
            if (this.tabPosition !== position) {
                this._set("tabPosition", position);
                this._tabBar.set(ButtonBar.ORIENTATION, this._orientationFromTabPosition(position));
                this._tabBar.set(TabBar.TAB_OPENING, Geometry.oppositeRegion(position));
                this._updateBarGrip(this._tabBar.orientation);
                this._updateContainerBorder();
                this._updateTabBarEdgeCoverVisibility();
                this.layout();

                // g1724939: Focus the selected child after changing the tab position
                if (this.selectedChildWidget) {
                    if (this.selectedChildWidget.focus) {
                        this.selectedChildWidget.focus();
                    } else {
                        this.selectedChildWidget.domNode.focus();
                    }
                }
            }
        },

        _setShrinkTabsToFitAttr: function (shrink) {
            this._tabBar.set(ButtonBar.SHRINK_WIDTH_TO_FIT, shrink);
        },

        _getShrinkTabsToFitAttr: function () {
            return this._tabBar.get(ButtonBar.SHRINK_WIDTH_TO_FIT);
        },

        _setMinimumTabWidthAttr: function (width) {
            this._tabBar.set(ButtonBar.MINIMUM_BUTTON_WIDTH, width);
        },

        _getMinimumTabWidthAttr: function () {
            return this._tabBar.get(ButtonBar.MINIMUM_BUTTON_WIDTH);
        },

        _setMaximumTabWidthAttr: function (width) {
            this._set(MAXIMUM_TAB_WIDTH, width);
            if (this.tabPosition === "left" || this.tabPosition === "right") {
                this.layout();
            }
        },

        _setHideSingleTabAttr: function (hide) {
            var tabsWereHidden, firstTabInfo;
            if (hide !== this.hideSingleTab) {
                tabsWereHidden = this._areTabsHidden;
                this._set(HIDE_SINGLE_TAB, hide);
                if (this._tabInfo.length === 1 && this._started) {
                    firstTabInfo = this._tabInfo[0];
                    if (hide) {
                        this._tabBar.removeTab(firstTabInfo.properties);
                        this._setAriaAttributesAfterHidingTab(firstTabInfo);
                    } else {
                        this._tabBar.addTab(firstTabInfo.widget, firstTabInfo.properties, firstTabInfo.initialIndex);
                        firstTabInfo.properties.set(LayoutChildProperty.IS_SHOWING, true);
                    }
                }
                this._updateTabsVisibility();
                if (tabsWereHidden !== this._areTabsHidden) {
                    this.layout();
                }
            } else {
                if (this._tabInfo.length === 1) {
                    this._setAriaAttributesAfterHidingTab(this._tabInfo[0]);
                }

            }
        },

        _setAriaAttributesAfterHidingTab: function (firstTabInfo) {
            if (firstTabInfo.properties && firstTabInfo.properties.title && firstTabInfo.widget
                && firstTabInfo.widget.domNode) {
                firstTabInfo.widget.domNode.removeAttribute("aria-labelledby");
                firstTabInfo.widget.domNode.setAttribute("aria-label", firstTabInfo.properties.title);
                firstTabInfo.widget.domNode.setAttribute("role", "region");
            }
        },

        _setShowingChildAttr: function (titleChildIdOrWidget) {
            this._showChild(this._getTabInfo(titleChildIdOrWidget));
        },

        _getShowingChildAttr: function () {
            return this._showingInfo ? this._showingInfo.widget : null;
        },

        _setPlusActionsAttr: function (actions) {
            var oldCount, newCount;
            if (actions.length === 0) {
                this._tabBar.setPlusButton(null);
            } else {
                if (!this.plusDropDownEnabled) {
                    actions = actions.slice(0, 1);
                }
                oldCount = Math.min(this.plusActions.length, 2);
                newCount = Math.min(actions.length, 2);
                if (oldCount === newCount) {
                    this._tabBar.getPlusButton().set("actions", actions);
                } else {
                    this._tabBar.setPlusButton(new TabPlusButton({actions: actions}));
                }
            }
            this._set(PLUS_ACTIONS, actions);
            this._updateTabsVisibility();
        },

        _setTabAnimationEnabledAttr: function (enabled) {
            this._tabBar.set("animationEnabled", enabled);
            this._set("tabAnimationEnabled", enabled);
        },

        _setTabBackgroundAttr: function (color) {
            var backgroundStyle = "background:" + color;
            var selectedStyle = "background: rgb(169,190,211)";
            this._addCustomStyle(BAR_COLOR_SELECTOR, backgroundStyle);
            this._addCustomStyle(TAB_COLOR_SELECTOR, backgroundStyle);
            this._addCustomStyle(SELECTED_BAR_COLOR_SELECTOR, selectedStyle);
            this._addCustomStyle(SELECTED_TAB_COLOR_SELECTOR, selectedStyle);
            this._set(TAB_BACKGROUND, color);
        },

        _setTabForegroundAttr: function (color) {
            this._addCustomStyle(TAB_COLOR_SELECTOR, "color:" + color);
            this._set(TAB_FOREGROUND, color);
        },

        _setShowingTabBackgroundAttr: function (color) {
            this._addCustomStyle(SHOWING_COLOR_SELECTOR, "background:" + color);
            this._addCustomStyle(SHOWING_BOTTOM_BORDER_SELECTOR, "border-bottom: 1px solid " + color);
            this._addCustomStyle(SHOWING_TOP_BORDER_SELECTOR, "border-top: 1px solid " + color);
            this._addCustomStyle(SHOWING_LEFT_BORDER_SELECTOR, "border-left: 1px solid " + color);
            this._addCustomStyle(SHOWING_RIGHT_BORDER_SELECTOR, "border-right: 1px solid " + color);
            this._set(SHOWING_TAB_BACKGROUND, color);
        },

        _setShowingTabForegroundAttr: function (color) {
            this._addCustomStyle(SHOWING_COLOR_SELECTOR, "color:" + color);
            this._set(SHOWING_TAB_FOREGROUND, color);
        },

        _addCustomStyle: function (selector, style) {
            if (this.id) {
                if (!this._styleSheet) {
                    this._styleSheet = document.createElement("style");
                    this._styleSheet.appendChild(document.createTextNode(""));  // Needed for WebKit
                    document.head.appendChild(this._styleSheet);
                }
                this._styleSheet.sheet.insertRule("#" + this.id + selector + " {" + style + "}", this._styleSheet.sheet.cssRules.length);
            } else {
                if (!this._pendingStyles) {
                    this._pendingStyles = [];
                }
                this._pendingStyles.push({selector: selector, style: style});
            }
        },

        _beginTabsDrag: function (e) {
            //  Only being drag if press was on grip or bar itself (not for example on the scroll buttons).
            if (mouse.isLeft(e) && !this._dragConnections &&
                (e.target === this._horizontalBarGrip || e.target === this._verticalBarGrip || e.target === this._tabBar.domNode)) {
                // Attach listeners
                this._dragConnections = [
                    on(this.ownerDocument, touch.move, lang.hitch(this, "_continueTabsDrag")),
                    on(this.ownerDocument, touch.release, lang.hitch(this, "_endTabsDrag"))
                ];
                SelectionBlocker.blockUntilRelease(this.domNode);
                this._hasDraggedOffTabs = false;
            }
            LayoutUtils.stopEvent(e);
        },

        _continueTabsDrag: function (e) {
            var containerBox = domGeometry.position(this.domNode);
            containerBox.l = containerBox.x;
            containerBox.t = containerBox.y;

            // Don't move tabs until drag off tab bar in its current position
            if (!this._hasDraggedOffTabs) {
                var barBox = domGeometry.position(this._tabBar.domNode);
                barBox.l = barBox.x;
                barBox.t = barBox.y;
                if (Geometry.boxContains(containerBox, e.pageX, e.pageY) && !Geometry.boxContains(barBox, e.pageX, e.pageY)) {
                    this._hasDraggedOffTabs = true;
                }
            }
            if (this._hasDraggedOffTabs) {
                var position = Geometry.getNearestBorder(containerBox, e.pageX, e.pageY, 0.25);
                if (position !== "center" && position !== this.tabPosition) {
                    this.set(TAB_POSITION, position);
                }
            }
        },

        _endTabsDrag: function () {
            this._dragConnections.forEach(function (connection) {
                connection.remove();
            });
            delete this._dragConnections;
            this.emit(LayoutContainerEvent.STATE_CHANGED);
        },

        on: function(type, func){
            // Forward drag out events from tab bar
            var that = this;
            if (type === LayoutContainerEvent.DRAG_OUT) {
                return this._tabBar.on(type, function (event) {
                    if (event.button) {
                        event.widget = event.button.widget;
                    }
                    event.box = that._getShowBox();
                    func(event);
                });
            }
            return this.inherited(arguments);
        },

        _getShowBox: function () {
            var box = lang.clone(this._showBox);
            var containerBox = domGeometry.position(this.domNode);
            box.l += containerBox.x;
            box.t += containerBox.y;
            return box;
        },

        _beginTabBarEdgeDrag: function (e) {
            // Initialize starting drag state
            if (mouse.isLeft(e) && this._showBox) {
                var contentBox = domGeometry.getContentBox(this.domNode, this.computedStyle);
                var edgeDragInfo = {
                    initialX     : e.pageX,
                    initialWidth : this.tabPosition === "left" ? this._showBox.l : (contentBox.w - this._showBox.w)
                };

                // Attach listeners
                if (mouse.isLeft(e)) {
                    edgeDragInfo.connections = [
                        on(this.ownerDocument, touch.move, lang.hitch(this, "_continueTabBarEdgeDrag", edgeDragInfo)),
                        on(this.ownerDocument, touch.release, lang.hitch(this, "_endTabBarEdgeDrag", edgeDragInfo))
                    ];
                    SelectionBlocker.blockUntilRelease(this.domNode);
                }
            }
        },

        _continueTabBarEdgeDrag: function (edgeDragInfo, e) {
            var change = this.tabPosition === "left" ? (e.pageX - edgeDragInfo.initialX) :
                                                       (edgeDragInfo.initialX - e.pageX);
            var updatedWidth = edgeDragInfo.initialWidth + change;
            updatedWidth = Math.max(updatedWidth, Math.max(MINIMUM_VERTICAL_TABS_WIDTH, this.get(MINIMUM_TAB_WIDTH)));
            this.set(MAXIMUM_TAB_WIDTH, updatedWidth);
        },

        _endTabBarEdgeDrag: function (edgeDragInfo) {
            edgeDragInfo.connections.forEach(function (connection) {
                connection.remove();
            });
            this.emit(LayoutContainerEvent.STATE_CHANGED);
        },

        _handleKeyDown: function (evt) {
            var keyCode = evt.keyCode;
            switch (true) {
                case keyCode === keys.TAB:
                    evt.preventDefault();
                    this._handleTabKey();
                    break;
                case keyCode === keys.LEFT_ARROW:
                    this._handleNavigation("previous");
                    break;
                case keyCode === keys.UP_ARROW:
                    // Up and down arrow key will only work for vertical navigation between the document container
                    // tabs. They should not do anything for horizontal navigation.
                    if (this.tabPosition === "left" || this.tabPosition === "right") {
                        this._handleNavigation("previous");
                    }
                    break;
                case keyCode === keys.RIGHT_ARROW:
                    this._handleNavigation("next");
                    break;
                case keyCode === keys.DOWN_ARROW:
                    if (this.tabPosition === "left" || this.tabPosition === "right") {
                        this._handleNavigation("next");
                    }
                    break;
                case keyCode === keys.ENTER:
                case keyCode === keys.SPACE:
                    var plusButton = this._tabBar.getPlusButton();
                    if (plusButton && plusButton.domNode && document.activeElement === plusButton.domNode) {
                        var plusIcon = plusButton.domNode.querySelector(".tabPlusIcon");
                        on.emit(plusIcon, "click", {bubbles: true});
                    }

                    var contextMenu = this._getContextMenu();
                    if (contextMenu && document.activeElement === contextMenu) {
                        on.emit(contextMenu, "pointerdown", {
                            bubbles: true,
                            button: 0, // for simulating left mouse click
                            cancelable: true,
                            target: contextMenu
                        });
                    }
                    break;
            }
        },

        _getContextMenu: function () {
            var contextMenu;
            if (this.tabPosition === "left" || this.tabPosition === "right") {
                var preNeighbor = this._tabBar.getPreNeighbor();
                contextMenu = preNeighbor.querySelector(".actionsButtonWrapper");
            } else if (this.tabPosition === "top" || this.tabPosition === "bottom") {
                contextMenu = this._tabBar.getPostNeighbor();
            }

            return contextMenu ? contextMenu : null;
        },

        _emitPointerEventOnTab: function (tab) {
            on.emit(tab, "pointerdown", {
                bubbles: true,
                button: 0, // for simulating left mouse click
                cancelable: true,
                target: tab
            });
            on.emit(tab, "pointerup", {
                bubbles: true,
                button: 0, // for simulating left mouse click
                cancelable: true,
                target: tab
            });
        },

        _deselectCurrentSelectedTab: function (selectionManager) {
            if (selectionManager[SelectionManager.SELECTED_CHILD]) {
                selectionManager.setSelected(selectionManager[SelectionManager.SELECTED_CHILD], false, true);
            }
        },

        _handleNavigation: function (direction) {
            var navigationTargets = [];
            var selectionData = [];
            var contextMenu;
            var parent = this.getParent();
            var selectionManager;

            while (!(parent && parent.selectionManager)) {
                parent = parent.getParent();
            }
            selectionManager = parent.selectionManager;

            // Add context menu - vertical tabs case
            if (this.tabPosition === "left" || this.tabPosition === "right") {
                contextMenu = this._getContextMenu();
                if (contextMenu) {
                    navigationTargets.push(contextMenu);
                }
            }

            var tabInfo = this._tabBar.getButtonInfo();
            tabInfo.forEach(function (tab) {
                if (tab.button && tab.button.domNode) {
                    // Add document container and bottom container tabs
                    navigationTargets.push(tab.button.domNode);
                    selectionData.push(tab);

                    // g2015705: Add actionNodeWrappers for Bottom Container so that the actions button becomes
                    // keyboard accessible
                    if (tab.button.actionsNodeWrapper) {
                        navigationTargets.push(tab.button.actionsNodeWrapper);
                        selectionData.push(tab);
                    }
                }
            });

            // Add plus button
            var plusButton = this._tabBar.getPlusButton();
            if (plusButton && plusButton.domNode) {
                navigationTargets.push(plusButton.domNode);
            }

            // Add context menu - horizontal tabs case
            if (this.tabPosition === "top" || this.tabPosition === "bottom") {
                contextMenu = this._getContextMenu();
                if (contextMenu) {
                    navigationTargets.push(contextMenu);
                }
            }

            // Bottom Container case. Add the collapse button.
            if (plusButton === null && contextMenu === null) {
                if (this.getParent && this.getParent() && this.getParent().getCollapseAllButton) {
                    navigationTargets.push(this.getParent().getCollapseAllButton());
                }
            }

            // Side container collapse button case
            if (navigationTargets.length < 2) {
                return;
            }

            // Determine the index of the currently focused element
            var focusedNode = document.activeElement;
            var focusedNodeIndex = navigationTargets.indexOf(focusedNode);

            switch (direction) {
                case "next":
                    // Update the tab content based on the tab selected.
                    if (this.tabPosition === "top" || this.tabPosition === "bottom") {
                        if (focusedNodeIndex === navigationTargets.length - 1) {
                            if (selectionManager._childProperties[selectionData[0].properties.id]) {
                                selectionManager.setSelected(selectionData[0].properties, true, true);
                            } else {
                                // Sub-tile condition - The group tab properties will not be in the
                                // childProperties of selectionManager, in that case, simulate a click event
                                this._emitPointerEventOnTab(navigationTargets[0]);
                            }

                        } else if (focusedNodeIndex < selectionData.length - 1) {
                            if (selectionManager._childProperties[selectionData[focusedNodeIndex + 1].properties.id]) {
                                selectionManager.setSelected(selectionData[focusedNodeIndex + 1].properties, true, true);
                            } else {
                                this._emitPointerEventOnTab(navigationTargets[focusedNodeIndex + 1]);
                            }
                        } else {
                            // Deselect the current selected tab
                            this._deselectCurrentSelectedTab(selectionManager);

                        }
                    } else if ((this.tabPosition === "left" || this.tabPosition === "right")
                        && (focusedNodeIndex < tabInfo.length)) {

                        if (selectionManager._childProperties[selectionData[focusedNodeIndex].properties.id]) {
                            selectionManager.setSelected(selectionData[focusedNodeIndex].properties, true, true);
                        } else {
                            this._emitPointerEventOnTab(navigationTargets[focusedNodeIndex + 1]);
                        }
                    } else {
                        // Deselect the current selected tab
                        this._deselectCurrentSelectedTab(selectionManager);

                    }

                    // Get the focus on the first tab
                    if (focusedNodeIndex === navigationTargets.length - 1) {
                        navigationTargets[0].focus();
                    }
                    // Get the focus on the next tab
                    else if (focusedNodeIndex >= 0) {
                        navigationTargets[focusedNodeIndex + 1].focus();
                    }
                    break;

                case "previous":
                    // Update the tab content based on the tab selected
                    if (this.tabPosition === "top" || this.tabPosition === "bottom") {
                        if (focusedNodeIndex > 0 && focusedNodeIndex <= selectionData.length) {
                            if (selectionManager._childProperties[selectionData[focusedNodeIndex - 1].properties.id]) {
                                selectionManager.setSelected(selectionData[focusedNodeIndex - 1].properties, true, true);
                            } else {
                                this._emitPointerEventOnTab(navigationTargets[focusedNodeIndex - 1]);
                            }
                        }

                        else if (focusedNodeIndex === 0) {
                            if (navigationTargets[navigationTargets.length - 1] === selectionData[selectionData.length - 1].button.domNode) {
                                if (selectionManager._childProperties[selectionData[selectionData.length - 1].properties.id]) {
                                    selectionManager.setSelected(selectionData[selectionData.length - 1].properties, true, true);
                                } else {
                                    this._emitPointerEventOnTab(navigationTargets[navigationTargets.length - 1]);
                                }
                            } else {
                                // Deselect the current selected tab
                                this._deselectCurrentSelectedTab(selectionManager);
                            }
                        } else {
                            // Deselect the current selected tab
                            this._deselectCurrentSelectedTab(selectionManager);
                        }
                    } else if (this.tabPosition === "left" || this.tabPosition === "right") {
                        if (focusedNodeIndex > 1 && focusedNodeIndex < navigationTargets.length) {
                            if (selectionManager._childProperties[selectionData[focusedNodeIndex - 2].properties.id]) {
                                selectionManager.setSelected(selectionData[focusedNodeIndex - 2].properties, true, true);
                            } else {
                                this._emitPointerEventOnTab(navigationTargets[focusedNodeIndex - 1]);
                            }
                        } else if (focusedNodeIndex === 0) {
                            if (navigationTargets[navigationTargets.length - 1] === selectionData[selectionData.length - 1].button.domNode) {
                                if (selectionManager._childProperties[selectionData[selectionData.length - 1].properties.id]) {
                                    selectionManager.setSelected(selectionData[selectionData.length - 1].properties, true, true);
                                } else {
                                    this._emitPointerEventOnTab(navigationTargets[navigationTargets.length - 1]);
                                }
                            }
                        } else {
                            // Deselect the current selected tab
                            this._deselectCurrentSelectedTab(selectionManager);
                        }
                    }

                    // Get the focus on the last tab
                    if (focusedNodeIndex === 0) {
                        navigationTargets[navigationTargets.length - 1].focus();
                    }
                    // Get the focus on the previous tab
                    else if (focusedNodeIndex > 0) {
                        navigationTargets[focusedNodeIndex - 1].focus();
                    }
                    break;
            }
        },

        _handleTabKey: function () {
            // Set focus on the content of the current tab
            var tabInfo = this._tabBar.getButtonInfo();
            var tabProperties;

            for (var tabIndex = 0; tabIndex < tabInfo.length; tabIndex++) {
                if (tabInfo[tabIndex].button && tabInfo[tabIndex].button.domNode
                    // The next clause will enable to tab into the tab content when the focus is on the button
                    // domNode or in the actionsNode
                    && tabInfo[tabIndex].button.domNode.contains(document.activeElement)) {
                    tabProperties = tabInfo[tabIndex].properties;
                    if (tabProperties) {
                        // From group tab to last selected tab within it
                        if (document.activeElement.classList.contains("groupTab")) {
                            if (tabProperties.groupProperties && tabProperties.groupProperties.lastSelected
                                && tabProperties.groupProperties.lastSelected.navAssociation
                                && tabProperties.groupProperties.lastSelected.navAssociation.focus) {
                                tabProperties.groupProperties.lastSelected.navAssociation.focus();
                            }
                        } else {
                            // DocumentContainer tab case
                            if (tabProperties.focus) {
                                tabProperties.focus();
                            } else if (tabProperties.domNode) {
                                tabProperties.domNode.focus();
                            }

                            // BottomContainer tab case
                            else if (tabProperties.widget) {
                                if (tabProperties.widget.focus) {
                                    tabProperties.widget.focus();
                                } else if (tabProperties.widget.domNode) {
                                    tabProperties.widget.domNode.focus();
                                }
                            }
                        }
                    }
                    break;
                }
            }
        }
    });

    /**
     * Returns true if the specified child (identified by its child id or title) is present in the given container state object.
     *
     * @param state {Object} opaque state object obtained from getState
     * @param childIdOrTitle {string} childId or title of interest
     * @returns {boolean} true if the specified child is found
     */
    TabContainer.stateIncludes = function (state, childIdOrTitle) {
        var i, savedChildInfo, savedId;
        for (i = 0; i < state.children.length; i++) {
            savedChildInfo = state.children[i];
            savedId = savedChildInfo.id || savedChildInfo.childId;
            if (!savedChildInfo.restored &&
                ((savedId && savedId === childIdOrTitle) || (!savedId && savedChildInfo.title === childIdOrTitle))) {
                return true;
            }
        }
        return false;
    };

    TabContainer.TAB_POSITION = TAB_POSITION;
    TabContainer.SHRINK_TABS_TO_FIT = SHRINK_TABS_TO_FIT;
    TabContainer.MINIMUM_TAB_WIDTH = MINIMUM_TAB_WIDTH;
    TabContainer.MAXIMUM_TAB_WIDTH = MAXIMUM_TAB_WIDTH;
    TabContainer.HIDE_SINGLE_TAB = HIDE_SINGLE_TAB;
    TabContainer.SUCCESSION_OPTION = SUCCESSION_OPTION;
    TabContainer.SHOWING_CHILD = SHOWING_CHILD;
    TabContainer.PLUS_ACTIONS = PLUS_ACTIONS;
    TabContainer.TAB_BACKGROUND = TAB_BACKGROUND;
    TabContainer.TAB_FOREGROUND = TAB_FOREGROUND;
    TabContainer.SHOWING_TAB_BACKGROUND = SHOWING_TAB_BACKGROUND;
    TabContainer.SHOWING_TAB_FOREGROUND = SHOWING_TAB_FOREGROUND;

    TabContainer.SUCCESSOR_NONE = NONE;
    TabContainer.SUCCESSOR_MOST_RECENT = MOST_RECENT;
    TabContainer.SUCCESSOR_NEIGHBOR = NEIGHBOR;

    return TabContainer;
});
